/**
 * Game catalog.
 *
 * `engine` maps a catalog entry to a React implementation registered in
 * `src/games/registry.ts`. Slots share a single engine and are differentiated
 * by `configId` (see `src/games/slots/configs.ts`). This keeps the lobby,
 * routing, search and favourites completely data-driven.
 */

export type GameCategory = 'slots' | 'table' | 'originals' | 'live' | 'poker' | 'instant';

export type GameEngine =
  | 'slots'
  | 'blackjack'
  | 'roulette'
  | 'baccarat'
  | 'craps'
  | 'sicbo'
  | 'threecardpoker'
  | 'casinowar'
  | 'videopoker'
  | 'holdem'
  | 'crash'
  | 'dice'
  | 'plinko'
  | 'mines'
  | 'keno'
  | 'wheel'
  | 'limbo'
  | 'hilo'
  | 'bingo';

export type Volatility = 'low' | 'medium' | 'high';
export type GameStatus = 'live' | 'coming-soon';
export type Presentation = 'standard' | 'live';

export interface GameArt {
  /** Gradient stops used to render procedural, asset-free tile art. */
  from: string;
  to: string;
  /** A single glyph/emoji representing the theme. */
  glyph: string;
  /** Optional subtle motif layered behind the glyph. */
  motif?: string;
}

export interface GameMeta {
  id: string;
  name: string;
  tagline: string;
  category: GameCategory;
  engine: GameEngine;
  /** Slots only — references a config in games/slots/configs.ts */
  configId?: string;
  /** Table/original variants (e.g. roulette ruleset, poker paytable). */
  variant?: string;
  provider: string;
  rtp: number;
  volatility: Volatility;
  minBet: number;
  maxBet: number;
  maxWinMultiplier: number;
  status: GameStatus;
  presentation?: Presentation;
  featured?: boolean;
  isNew?: boolean;
  releaseDate: string;
  art: GameArt;
  badges?: string[];
}

export interface GameCategoryMeta {
  id: GameCategory;
  label: string;
  blurb: string;
  icon: string;
}

export const GAME_CATEGORIES: GameCategoryMeta[] = [
  {
    id: 'slots',
    label: 'Slots',
    blurb: 'Reels, ways and megaways across classic and video themes.',
    icon: 'sparkles',
  },
  {
    id: 'table',
    label: 'Table',
    blurb: 'Blackjack, roulette, baccarat, craps and more.',
    icon: 'diamond',
  },
  {
    id: 'originals',
    label: 'Originals',
    blurb: 'Provably-fair instant games built in-house.',
    icon: 'zap',
  },
  {
    id: 'live',
    label: 'Live',
    blurb: 'Studio-style dealers with real-time presentation.',
    icon: 'radio',
  },
  {
    id: 'poker',
    label: 'Poker',
    blurb: 'Video poker paytables and heads-up hold’em.',
    icon: 'club',
  },
  {
    id: 'instant',
    label: 'Instant',
    blurb: 'Fast wins — bingo, war and scratch cards.',
    icon: 'ticket',
  },
];

const REPLAYABLE = '2025-01-01';

export const GAMES: GameMeta[] = [
  /* ------------------------------- Slots -------------------------------- */
  {
    id: 'agave-gold',
    name: 'Agave Gold',
    tagline: '243 ways, blooming wild multipliers',
    category: 'slots',
    engine: 'slots',
    configId: 'agaveGold',
    provider: 'Agave Originals',
    rtp: 0.964,
    volatility: 'high',
    minBet: 0.1,
    maxBet: 500,
    maxWinMultiplier: 12500,
    status: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#00c98c', to: '#ffbe1f', glyph: '🌵', motif: '✶' },
    badges: ['Featured', 'Megaways'],
  },
  {
    id: 'desert-fortune',
    name: 'Desert Fortune',
    tagline: 'Three-reel classic with a gilded edge',
    category: 'slots',
    engine: 'slots',
    configId: 'desertFortune',
    provider: 'Saguaro Studios',
    rtp: 0.958,
    volatility: 'low',
    minBet: 0.1,
    maxBet: 200,
    maxWinMultiplier: 2000,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#b75206', to: '#7a350d', glyph: '🍒', motif: '◆' },
    badges: ['Classic'],
  },
  {
    id: 'lucky-cactus',
    name: 'Lucky Cactus',
    tagline: '117,649 ways to bloom',
    category: 'slots',
    engine: 'slots',
    configId: 'luckyCactus',
    provider: 'Agave Originals',
    rtp: 0.961,
    volatility: 'high',
    minBet: 0.2,
    maxBet: 400,
    maxWinMultiplier: 25000,
    status: 'live',
    featured: true,
    isNew: true,
    releaseDate: '2026-06-01',
    art: { from: '#085641', to: '#24e2a4', glyph: '🌵', motif: '≡' },
    badges: ['Megaways'],
  },
  {
    id: 'book-of-sun',
    name: 'Book of Sun',
    tagline: 'Expanding symbols in the free-spin tomb',
    category: 'slots',
    engine: 'slots',
    configId: 'bookOfSun',
    provider: 'Desert Bloom Gaming',
    rtp: 0.962,
    volatility: 'high',
    minBet: 0.1,
    maxBet: 250,
    maxWinMultiplier: 5000,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#dd7602', to: '#461a02', glyph: '📖', motif: '☀' },
    badges: ['Free spins'],
  },
  {
    id: 'fruit-fiesta',
    name: 'Fruit Fiesta Deluxe',
    tagline: 'Retro fruit with respins and stacked wilds',
    category: 'slots',
    engine: 'slots',
    configId: 'fruitFiesta',
    provider: 'Nocturne Labs',
    rtp: 0.96,
    volatility: 'medium',
    minBet: 0.1,
    maxBet: 300,
    maxWinMultiplier: 4000,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#ef4565', to: '#7a350d', glyph: '🍇', motif: '✕' },
  },
  {
    id: 'neon-nights',
    name: 'Neon Nights',
    tagline: '1024 ways through the electric city',
    category: 'slots',
    engine: 'slots',
    configId: 'neonNights',
    provider: 'Nocturne Labs',
    rtp: 0.963,
    volatility: 'high',
    minBet: 0.2,
    maxBet: 400,
    maxWinMultiplier: 10000,
    status: 'live',
    isNew: true,
    releaseDate: '2026-05-12',
    art: { from: '#7c3aed', to: '#35b6ff', glyph: '🌃', motif: '≈' },
    badges: ['1024 ways'],
  },
  {
    id: 'golden-scarab',
    name: 'Golden Scarab',
    tagline: 'Hold-and-win jackpot reels',
    category: 'slots',
    engine: 'slots',
    configId: 'goldenScarab',
    provider: 'Desert Bloom Gaming',
    rtp: 0.959,
    volatility: 'medium',
    minBet: 0.1,
    maxBet: 300,
    maxWinMultiplier: 6000,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#ffbe1f', to: '#085641', glyph: '🪲', motif: '⌘' },
    badges: ['Jackpot'],
  },
  {
    id: 'crystal-caverns',
    name: 'Crystal Caverns',
    tagline: 'Cluster pays in a glowing grotto',
    category: 'slots',
    engine: 'slots',
    configId: 'crystalCaverns',
    provider: 'Saguaro Studios',
    rtp: 0.961,
    volatility: 'medium',
    minBet: 0.1,
    maxBet: 250,
    maxWinMultiplier: 7500,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#35b6ff', to: '#7c3aed', glyph: '💎', motif: '✦' },
  },
  {
    id: 'saguaro-wilds',
    name: 'Saguaro Wilds',
    tagline: 'Stacked wilds across the sunset trail',
    category: 'slots',
    engine: 'slots',
    configId: 'saguaroWilds',
    provider: 'Saguaro Studios',
    rtp: 0.96,
    volatility: 'medium',
    minBet: 0.1,
    maxBet: 250,
    maxWinMultiplier: 5000,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#f9a007', to: '#943f0c', glyph: '🌅', motif: '≋' },
  },
  {
    id: 'dragons-hoard',
    name: "Dragon's Hoard",
    tagline: 'Four tiers of progressive jackpots',
    category: 'slots',
    engine: 'slots',
    configId: 'dragonsHoard',
    provider: 'Agave Originals',
    rtp: 0.965,
    volatility: 'high',
    minBet: 0.2,
    maxBet: 500,
    maxWinMultiplier: 20000,
    status: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#d92d4c', to: '#461a02', glyph: '🐉', motif: '❖' },
    badges: ['Jackpot'],
  },
  {
    id: 'cosmic-cash',
    name: 'Cosmic Cash',
    tagline: 'Falling symbols across the galaxy',
    category: 'slots',
    engine: 'slots',
    configId: 'cosmicCash',
    provider: 'Nocturne Labs',
    rtp: 0.962,
    volatility: 'high',
    minBet: 0.2,
    maxBet: 400,
    maxWinMultiplier: 15000,
    status: 'live',
    isNew: true,
    releaseDate: '2026-07-04',
    art: { from: '#1d4ed8', to: '#35b6ff', glyph: '🪐', motif: '◌' },
  },
  {
    id: 'sugar-skull',
    name: 'Sugar Skull Fiesta',
    tagline: 'Día de los Muertos with wild reels',
    category: 'slots',
    engine: 'slots',
    configId: 'sugarSkull',
    provider: 'Desert Bloom Gaming',
    rtp: 0.96,
    volatility: 'medium',
    minBet: 0.1,
    maxBet: 300,
    maxWinMultiplier: 8000,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#ef4565', to: '#f9a007', glyph: '💀', motif: '✿' },
  },

  /* ------------------------------- Table -------------------------------- */
  {
    id: 'blackjack-classic',
    name: 'Blackjack Classic',
    tagline: 'Six decks, dealer stands on all 17s',
    category: 'table',
    engine: 'blackjack',
    variant: 'classic',
    provider: 'Agave Originals',
    rtp: 0.995,
    volatility: 'low',
    minBet: 1,
    maxBet: 5000,
    maxWinMultiplier: 2.5,
    status: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#00835f', to: '#0a5136', glyph: '🂡', motif: '♠' },
    badges: ['Table favourite'],
  },
  {
    id: 'blackjack-vegas',
    name: 'Vegas Blackjack',
    tagline: 'Double deck, dealer hits soft 17, 3:2',
    category: 'table',
    engine: 'blackjack',
    variant: 'vegas',
    provider: 'Agave Originals',
    rtp: 0.994,
    volatility: 'low',
    minBet: 1,
    maxBet: 5000,
    maxWinMultiplier: 2.5,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#085641', to: '#ffbe1f', glyph: '🂮', motif: '♦' },
  },
  {
    id: 'roulette-european',
    name: 'European Roulette',
    tagline: 'Single zero, the player-favourite wheel',
    category: 'table',
    engine: 'roulette',
    variant: 'european',
    provider: 'Agave Originals',
    rtp: 0.973,
    volatility: 'medium',
    minBet: 0.5,
    maxBet: 5000,
    maxWinMultiplier: 36,
    status: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#00835f', to: '#002e23', glyph: '🎡', motif: '◍' },
  },
  {
    id: 'roulette-american',
    name: 'American Roulette',
    tagline: 'Double zero, bigger swings',
    category: 'table',
    engine: 'roulette',
    variant: 'american',
    provider: 'Agave Originals',
    rtp: 0.947,
    volatility: 'medium',
    minBet: 0.5,
    maxBet: 5000,
    maxWinMultiplier: 36,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#d92d4c', to: '#002e23', glyph: '🎯', motif: '◎' },
  },
  {
    id: 'baccarat-punto',
    name: 'Baccarat Punto Banco',
    tagline: 'Player, Banker or Tie — pure chance',
    category: 'table',
    engine: 'baccarat',
    variant: 'punto-banco',
    provider: 'Agave Originals',
    rtp: 0.989,
    volatility: 'low',
    minBet: 1,
    maxBet: 10000,
    maxWinMultiplier: 9,
    status: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#085641', to: '#00c98c', glyph: '🀄', motif: '◆' },
  },
  {
    id: 'craps',
    name: 'Craps',
    tagline: 'Pass line, odds and the full felt',
    category: 'table',
    engine: 'craps',
    variant: 'standard',
    provider: 'Agave Originals',
    rtp: 0.986,
    volatility: 'medium',
    minBet: 1,
    maxBet: 5000,
    maxWinMultiplier: 30,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#0f7a4f', to: '#0a5136', glyph: '🎲', motif: '⚄' },
  },
  {
    id: 'sic-bo',
    name: 'Sic Bo',
    tagline: 'Three dice, dozens of ways to win',
    category: 'table',
    engine: 'sicbo',
    variant: 'standard',
    provider: 'Agave Originals',
    rtp: 0.972,
    volatility: 'high',
    minBet: 0.5,
    maxBet: 5000,
    maxWinMultiplier: 180,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#b75206', to: '#0a5136', glyph: '🎴', motif: '⚂' },
  },
  {
    id: 'three-card-poker',
    name: 'Three Card Poker',
    tagline: 'Ante, play and pair-plus side bets',
    category: 'table',
    engine: 'threecardpoker',
    variant: 'standard',
    provider: 'Agave Originals',
    rtp: 0.978,
    volatility: 'medium',
    minBet: 1,
    maxBet: 2500,
    maxWinMultiplier: 40,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#7c3aed', to: '#085641', glyph: '🃏', motif: '♣' },
  },

  /* ----------------------------- Originals ------------------------------ */
  {
    id: 'crash',
    name: 'Crash',
    tagline: 'Cash out before the curve busts',
    category: 'originals',
    engine: 'crash',
    provider: 'Agave Originals',
    rtp: 0.99,
    volatility: 'high',
    minBet: 0.1,
    maxBet: 5000,
    maxWinMultiplier: 1_000_000,
    status: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#00c98c', to: '#7c3aed', glyph: '🚀', motif: '📈' },
    badges: ['Provably fair'],
  },
  {
    id: 'dice',
    name: 'Dice',
    tagline: 'Roll under or over — tune your own odds',
    category: 'originals',
    engine: 'dice',
    provider: 'Agave Originals',
    rtp: 0.99,
    volatility: 'medium',
    minBet: 0.1,
    maxBet: 5000,
    maxWinMultiplier: 990,
    status: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#35b6ff', to: '#085641', glyph: '🎲', motif: '⚄' },
    badges: ['Provably fair'],
  },
  {
    id: 'plinko',
    name: 'Plinko',
    tagline: 'Drop the ball, ride the multipliers',
    category: 'originals',
    engine: 'plinko',
    provider: 'Agave Originals',
    rtp: 0.99,
    volatility: 'high',
    minBet: 0.1,
    maxBet: 1000,
    maxWinMultiplier: 1000,
    status: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#ef4565', to: '#ffbe1f', glyph: '🔻', motif: '•••' },
    badges: ['Provably fair'],
  },
  {
    id: 'mines',
    name: 'Mines',
    tagline: 'Reveal gems, dodge the mines',
    category: 'originals',
    engine: 'mines',
    provider: 'Agave Originals',
    rtp: 0.99,
    volatility: 'high',
    minBet: 0.1,
    maxBet: 5000,
    maxWinMultiplier: 2500,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#24e2a4', to: '#d92d4c', glyph: '💣', motif: '✦' },
    badges: ['Provably fair'],
  },
  {
    id: 'keno',
    name: 'Keno',
    tagline: 'Pick your numbers, watch them land',
    category: 'originals',
    engine: 'keno',
    provider: 'Agave Originals',
    rtp: 0.97,
    volatility: 'medium',
    minBet: 0.1,
    maxBet: 1000,
    maxWinMultiplier: 1000,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#7c3aed', to: '#35b6ff', glyph: '🔢', motif: '⌗' },
  },
  {
    id: 'wheel',
    name: 'Wheel of Agave',
    tagline: 'Segmented wheel with multiplier pays',
    category: 'originals',
    engine: 'wheel',
    provider: 'Agave Originals',
    rtp: 0.96,
    volatility: 'medium',
    minBet: 0.1,
    maxBet: 1000,
    maxWinMultiplier: 50,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#ffbe1f', to: '#7c3aed', glyph: '🎡', motif: '✷' },
  },
  {
    id: 'limbo',
    name: 'Limbo',
    tagline: 'Set a target, beat the bust point',
    category: 'originals',
    engine: 'limbo',
    provider: 'Agave Originals',
    rtp: 0.99,
    volatility: 'high',
    minBet: 0.1,
    maxBet: 5000,
    maxWinMultiplier: 1_000_000,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#24e2a4', to: '#1d4ed8', glyph: '📶', motif: '↗' },
    badges: ['Provably fair'],
  },
  {
    id: 'hilo',
    name: 'Hi-Lo',
    tagline: 'Call the next card, compound your streak',
    category: 'originals',
    engine: 'hilo',
    provider: 'Agave Originals',
    rtp: 0.98,
    volatility: 'medium',
    minBet: 0.1,
    maxBet: 2500,
    maxWinMultiplier: 1000,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#d92d4c', to: '#085641', glyph: '🃏', motif: '↕' },
  },

  /* ------------------------------- Poker -------------------------------- */
  {
    id: 'video-poker-jacks',
    name: 'Video Poker: Jacks or Better',
    tagline: 'Draw poker with a 9/6 paytable',
    category: 'poker',
    engine: 'videopoker',
    variant: 'jacks-or-better',
    provider: 'Agave Originals',
    rtp: 0.9954,
    volatility: 'low',
    minBet: 0.25,
    maxBet: 500,
    maxWinMultiplier: 800,
    status: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#00835f', to: '#35b6ff', glyph: '♠', motif: '♥' },
  },
  {
    id: 'video-poker-deuces',
    name: 'Video Poker: Deuces Wild',
    tagline: 'Every deuce is wild',
    category: 'poker',
    engine: 'videopoker',
    variant: 'deuces-wild',
    provider: 'Agave Originals',
    rtp: 0.9939,
    volatility: 'medium',
    minBet: 0.25,
    maxBet: 500,
    maxWinMultiplier: 1000,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#7c3aed', to: '#d92d4c', glyph: '♣', motif: '♦' },
  },
  {
    id: 'texas-holdem',
    name: "Hold'em Heads-Up",
    tagline: 'Take on the dealer at the river',
    category: 'poker',
    engine: 'holdem',
    variant: 'heads-up',
    provider: 'Agave Originals',
    rtp: 0.98,
    volatility: 'high',
    minBet: 1,
    maxBet: 5000,
    maxWinMultiplier: 50,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#085641', to: '#ffbe1f', glyph: '♠', motif: '♣' },
  },

  /* ------------------------------- Instant ------------------------------ */
  {
    id: 'casino-war',
    name: 'Casino War',
    tagline: 'Highest card wins — or go to war',
    category: 'instant',
    engine: 'casinowar',
    provider: 'Agave Originals',
    rtp: 0.977,
    volatility: 'low',
    minBet: 0.5,
    maxBet: 2500,
    maxWinMultiplier: 20,
    status: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#d92d4c', to: '#085641', glyph: '⚔️', motif: '♠' },
  },
  {
    id: 'bingo',
    name: 'Agave Bingo',
    tagline: 'Ninety balls, three lines to win',
    category: 'instant',
    engine: 'bingo',
    provider: 'Agave Originals',
    rtp: 0.95,
    volatility: 'medium',
    minBet: 0.25,
    maxBet: 250,
    maxWinMultiplier: 500,
    status: 'live',
    isNew: true,
    releaseDate: '2026-08-01',
    art: { from: '#00c98c', to: '#f9a007', glyph: '🎱', motif: '◯' },
  },
  {
    id: 'scratch-gold',
    name: 'Scratch Gold',
    tagline: 'Instant reveal, three-of-a-kind pays',
    category: 'instant',
    engine: 'bingo',
    variant: 'scratch',
    provider: 'Agave Originals',
    rtp: 0.94,
    volatility: 'high',
    minBet: 0.25,
    maxBet: 100,
    maxWinMultiplier: 1000,
    status: 'coming-soon',
    releaseDate: '2026-11-01',
    art: { from: '#ffbe1f', to: '#943f0c', glyph: '🪙', motif: '✧' },
  },

  /* -------------------------------- Live -------------------------------- */
  {
    id: 'live-blackjack',
    name: 'Live Blackjack',
    tagline: 'Studio dealer, six-deck shoe',
    category: 'live',
    engine: 'blackjack',
    variant: 'classic',
    provider: 'Agave Live',
    rtp: 0.995,
    volatility: 'low',
    minBet: 5,
    maxBet: 10000,
    maxWinMultiplier: 2.5,
    status: 'live',
    presentation: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#0a5136', to: '#002e23', glyph: '🃏', motif: '♠' },
    badges: ['Live'],
  },
  {
    id: 'live-roulette',
    name: 'Lightning Roulette',
    tagline: 'Lucky numbers with boosted multipliers',
    category: 'live',
    engine: 'roulette',
    variant: 'lightning',
    provider: 'Agave Live',
    rtp: 0.971,
    volatility: 'high',
    minBet: 1,
    maxBet: 5000,
    maxWinMultiplier: 500,
    status: 'live',
    presentation: 'live',
    featured: true,
    releaseDate: REPLAYABLE,
    art: { from: '#ffbe1f', to: '#7c3aed', glyph: '⚡', motif: '◍' },
    badges: ['Live'],
  },
  {
    id: 'live-baccarat',
    name: 'Live Baccarat',
    tagline: 'Speed baccarat from the studio floor',
    category: 'live',
    engine: 'baccarat',
    variant: 'punto-banco',
    provider: 'Agave Live',
    rtp: 0.989,
    volatility: 'low',
    minBet: 5,
    maxBet: 20000,
    maxWinMultiplier: 9,
    status: 'live',
    presentation: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#085641', to: '#00c98c', glyph: '🀄', motif: '◆' },
    badges: ['Live'],
  },
  {
    id: 'live-dream-catcher',
    name: 'Dream Catcher',
    tagline: 'Money wheel with 40x multiplier segments',
    category: 'live',
    engine: 'wheel',
    variant: 'money-wheel',
    provider: 'Agave Live',
    rtp: 0.958,
    volatility: 'medium',
    minBet: 1,
    maxBet: 5000,
    maxWinMultiplier: 40,
    status: 'live',
    presentation: 'live',
    releaseDate: REPLAYABLE,
    art: { from: '#35b6ff', to: '#ffbe1f', glyph: '🎯', motif: '✷' },
    badges: ['Live'],
  },
];

export const GAMES_BY_ID: Record<string, GameMeta> = Object.fromEntries(
  GAMES.map((game) => [game.id, game]),
);

export function gamesByCategory(category: GameCategory): GameMeta[] {
  return GAMES.filter((game) => game.category === category);
}

export function featuredGames(): GameMeta[] {
  return GAMES.filter((game) => game.featured);
}

export function liveGames(): GameMeta[] {
  return GAMES.filter((game) => game.status === 'live');
}

export function searchGames(query: string): GameMeta[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return GAMES.filter(
    (game) =>
      game.name.toLowerCase().includes(q) ||
      game.tagline.toLowerCase().includes(q) ||
      game.provider.toLowerCase().includes(q) ||
      game.category.includes(q),
  );
}
